import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { BellRing, Lock, LogOut, Users, UserCheck, UserX, ListChecks, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, tone, testId }) => (
  <div data-testid={testId} className="bg-white rounded-2xl p-5 border border-stone-200/70 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
    <div className={`w-10 h-10 rounded-full ${tone} flex items-center justify-center mb-3`}>
      <Icon size={18} />
    </div>
    <p className="font-display font-extrabold text-3xl text-[#1E2022]">{value}</p>
    <p className="font-mono2 text-[11px] tracking-[0.15em] uppercase text-[#8A8F98] mt-1">{label}</p>
  </div>
);

const Table = ({ testId, head, rows, empty }) => (
  <div data-testid={testId} className="bg-white rounded-2xl border border-stone-200/70 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-stone-200/70 bg-[#FAF8F5]">
            {head.map((h) => (
              <th key={h} className="text-left font-mono2 text-[10px] tracking-[0.15em] uppercase text-[#8A8F98] px-5 py-3">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={head.length} className="px-5 py-8 text-center text-[#8A8F98] text-sm">
                {empty}
              </td>
            </tr>
          ) : (
            rows
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const StatusPill = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
      active ? "bg-[#4A7C59]/10 text-[#4A7C59]" : "bg-[#D96B43]/10 text-[#C25832]"
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-[#4A7C59]" : "bg-[#D96B43]"}`} />
    {active ? "Opted in" : "Opted out"}
  </span>
);

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("rmd_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (tok) => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/overview`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        setData(res.data);
      } catch (e) {
        sessionStorage.removeItem("rmd_admin_token");
        setToken("");
        setData(null);
        setError("Session expired. Please sign in again.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("rmd_admin_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Sign in failed.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("rmd_admin_token");
    setToken("");
    setData(null);
  };

  if (!token) {
    return (
      <div data-testid="admin-page" className="min-h-screen bg-[#FAF8F5] grain flex items-center justify-center px-5">
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={login}
          data-testid="admin-login-form"
          className="relative z-10 w-full max-w-sm bg-white rounded-3xl p-8 border border-stone-200/70 shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
        >
          <div className="w-11 h-11 rounded-full bg-[#D96B43] text-white flex items-center justify-center mb-5">
            <Lock size={19} />
          </div>
          <h1 className="font-display font-extrabold text-2xl text-[#1E2022] mb-1.5">Subscriber console</h1>
          <p className="text-sm text-[#575B60] mb-6">Private view — enter the admin password.</p>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="w-full rounded-xl border border-stone-200 bg-[#FAF8F5] px-4 py-3 text-sm text-[#1E2022] outline-none focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/15 transition mb-4"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-600 mb-4">{error}</p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            className="w-full font-display font-bold bg-[#1E2022] text-[#FAF8F5] rounded-full px-6 py-3.5 text-sm hover:bg-[#D96B43] transition-colors duration-300"
          >
            Sign in
          </button>
          <Link to="/" data-testid="admin-back-home" className="mt-5 flex items-center justify-center gap-1.5 text-xs text-[#8A8F98] hover:text-[#D96B43] transition-colors">
            <ArrowLeft size={13} /> Back to site
          </Link>
        </motion.form>
      </div>
    );
  }

  const counts = data?.counts || { subscribers: 0, opted_in: 0, opted_out: 0, tasks: 0 };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F5] grain">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FAF8F5]/85 border-b border-stone-200/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-full bg-[#D96B43] text-[#FAF8F5] flex items-center justify-center">
              <BellRing size={14} strokeWidth={2.4} />
            </span>
            <span className="font-display font-extrabold tracking-tight text-[#1E2022]">
              RemindMe <span className="text-[#D96B43]">&</span> Do
              <span className="ml-2 font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#8A8F98] font-normal">Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-nav-home" className="text-sm font-medium text-[#575B60] hover:text-[#D96B43] transition-colors">
              View site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 text-sm font-medium text-[#575B60] hover:text-[#C25832] transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#1E2022] mb-8" data-testid="admin-heading">
          Subscribers & tasks
        </h1>

        {loading && !data ? (
          <p className="text-sm text-[#8A8F98]" data-testid="admin-loading">Loading…</p>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total subscribers" value={counts.subscribers} tone="bg-[#1E2022]/5 text-[#1E2022]" testId="stat-subscribers" />
              <StatCard icon={UserCheck} label="Opted in" value={counts.opted_in} tone="bg-[#4A7C59]/10 text-[#4A7C59]" testId="stat-opted-in" />
              <StatCard icon={UserX} label="Opted out" value={counts.opted_out} tone="bg-[#D96B43]/10 text-[#C25832]" testId="stat-opted-out" />
              <StatCard icon={ListChecks} label="Tasks received" value={counts.tasks} tone="bg-[#E5A93C]/15 text-[#B07E1E]" testId="stat-tasks" />
            </div>

            <section>
              <h2 className="font-display font-bold text-xl text-[#1E2022] mb-4">Subscribers</h2>
              <Table
                testId="subscribers-table"
                head={["Phone", "Status", "Source", "Last update"]}
                empty="No subscribers yet — text START to your number to see the first one."
                rows={(data?.subscribers || []).map((s) => (
                  <tr key={s.phone} className="border-b border-stone-100 last:border-0">
                    <td className="px-5 py-3.5 font-mono2 text-xs sm:text-sm text-[#1E2022]">{s.phone}</td>
                    <td className="px-5 py-3.5"><StatusPill active={s.opted_in} /></td>
                    <td className="px-5 py-3.5 text-[#575B60]">{s.method === "keyword_text" ? "Texted START" : "Website form"}</td>
                    <td className="px-5 py-3.5 text-[#8A8F98] text-xs">{fmt(s.updated_at)}</td>
                  </tr>
                ))}
              />
            </section>

            <section>
              <h2 className="font-display font-bold text-xl text-[#1E2022] mb-4">Tasks received by text</h2>
              <Table
                testId="tasks-table"
                head={["Phone", "Task", "Status", "Remind at"]}
                empty="No tasks yet — subscribers can text any task to your number."
                rows={(data?.tasks || []).map((t) => (
                  <tr key={t.id} className="border-b border-stone-100 last:border-0">
                    <td className="px-5 py-3.5 font-mono2 text-xs sm:text-sm text-[#1E2022] whitespace-nowrap">{t.phone}</td>
                    <td className="px-5 py-3.5 text-[#575B60]">{t.body}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                        t.status === "sent" || t.status === "done" ? "bg-[#4A7C59]/10 text-[#4A7C59]"
                        : t.status === "scheduled" ? "bg-[#E5A93C]/15 text-[#B07E1E]"
                        : "bg-stone-200/70 text-[#575B60]"
                      }`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-[#8A8F98] text-xs whitespace-nowrap">{fmt(t.remind_at)}</td>
                  </tr>
                ))}
              />
            </section>

            <section>
              <h2 className="font-display font-bold text-xl text-[#1E2022] mb-4">Website sign-ups</h2>
              <Table
                testId="optins-table"
                head={["Name", "Phone", "Preference", "Signed up"]}
                empty="No website sign-ups yet."
                rows={(data?.optins || []).map((o) => (
                  <tr key={o.id} className="border-b border-stone-100 last:border-0">
                    <td className="px-5 py-3.5 text-[#1E2022] font-medium">{o.name}</td>
                    <td className="px-5 py-3.5 font-mono2 text-xs sm:text-sm text-[#575B60]">{o.phone}</td>
                    <td className="px-5 py-3.5 text-[#575B60]">{o.task_preference || "—"}</td>
                    <td className="px-5 py-3.5 text-[#8A8F98] text-xs whitespace-nowrap">{fmt(o.created_at)}</td>
                  </tr>
                ))}
              />
            </section>
          </motion.div>
        )}
      </main>
    </div>
  );
}
